import React, { useState } from "react";
import { Card, CardBody, Container, Badge } from "reactstrap";
import { useDispatch, useSelector } from "react-redux";
import { loadingTrue, loadingFalse } from "../../Reducer/loaderSlice";
import Handler from "../../Helpers/Handler";
import { toast } from "react-toastify";
import AdminHeader from "./Header";
import { Megaphone, Send, ShieldCheck, Info } from "lucide-react";

// const BASE = "http://localhost/business_community/";
const BASE = "https://qnexus.in/community/";
const FALLBACK_AVATAR = "https://ui-avatars.com/api/?background=1e3a8a&color=fff&name=";

const AdminAnnouncement = () => {
    const dispatch = useDispatch();
    const admin = useSelector(s => s.auth?.user);
    const [message, setMessage] = useState("");

    const sendAnnouncement = async () => {
        if (!message.trim()) {
            toast.error("Announcement message is required");
            return;
        }

        dispatch(loadingTrue());
        try {
            const res = await Handler({
                method: "post",
                url: "/admin/announcement.php",
                data: { message }
            });

            if (res?.success) {
                toast.success(`Announcement sent to ${res.data.sent_to} businesses`);
                setMessage("");
            } else {
                toast.error(res?.message || "Failed to send");
            }
        } catch (e) {
            Handler(e);
            toast.error("Server error");
        }
        dispatch(loadingFalse());
    };

    return (
        <div className="bg-soft-blue min-vh-100">
            <AdminHeader />
            <Container py="4" className="mt-4 pb-5">
                
                {/* PAGE TITLE */}
                <div className="d-flex align-items-center mb-4">
                    <div className="icon-box-blue me-3 shadow-sm">
                        <Megaphone size={22} />
                    </div>
                    <div>
                        <h3 className="fw-bold text-navy mb-0">Broadcast Announcement</h3>
                        <p className="text-muted small mb-0">Reach all registered business entities instantly</p>
                    </div>
                </div>

                <div className="row justify-content-center">
                    <div className="col-lg-8">
                        <Card className="border-0 shadow-sm rounded-4 overflow-hidden">
                            {/* BLUE ACCENT TOP BAR */}
                            <div className="bg-navy py-1"></div>
                            
                            <CardBody className="p-4">
                                {/* ADMIN INFO HEADER */}
                                <div className="d-flex align-items-center justify-content-between mb-4 pb-3 border-bottom">
                                    <div className="d-flex align-items-center gap-3">
                                        <div className="position-relative">
                                            <img
                                                src={admin?.image ? BASE + admin.image : `${FALLBACK_AVATAR}${admin?.name || 'A'}`}
                                                className="rounded-circle border shadow-sm"
                                                style={{ width: 55, height: 55, objectFit: "cover" }}
                                                alt="Admin"
                                            />
                                            <span className="position-absolute bottom-0 end-0 bg-success border border-2 border-white rounded-circle" style={{ width: 14, height: 14 }}></span>
                                        </div>
                                        <div>
                                            <h6 className="fw-bold text-navy mb-0">{admin?.name || "Administrator"}</h6>
                                            <div className="d-flex align-items-center text-success x-small fw-bold">
                                                <ShieldCheck size={12} className="me-1" /> Verified Admin Account
                                            </div>
                                        </div>
                                    </div>
                                    <Badge color="light" className="text-primary border border-primary-subtle rounded-pill px-3 py-2">
                                        Global Broadcast Mode
                                    </Badge>
                                </div>

                                {/* INPUT AREA */}
                                <div className="mb-4">
                                    <label className="fw-bold text-muted small mb-2 d-flex align-items-center">
                                        Announcement Content
                                    </label>
                                    <textarea
                                        className="form-control custom-textarea"
                                        placeholder="Type your message here... All businesses will receive this notification."
                                        value={message}
                                        onChange={e => setMessage(e.target.value)}
                                    />
                                    <div className="mt-2 d-flex align-items-center text-muted small">
                                        <Info size={14} className="me-1 text-primary" />
                                        <span>This message will be visible to every active business profile in the community.</span>
                                    </div>
                                </div>

                                {/* ACTION BUTTON */}
                                <div className="d-flex justify-content-end">
                                    <button
                                        onClick={sendAnnouncement}
                                        className="btn btn-navy d-flex align-items-center gap-2 px-4 py-2 rounded-3 shadow-sm hover-up"
                                        disabled={!message.trim()}
                                    >
                                        <Send size={18} />
                                        <span>Send Announcement</span>
                                    </button>
                                </div>
                            </CardBody>
                        </Card>
                    </div>
                </div>
            </Container>

            <style>{`
                .bg-soft-blue { background-color: #f0f4f8; }
                .text-navy { color: #1a365d; }
                .bg-navy { background-color: #1e3a8a; }
                .x-small { font-size: 0.75rem; }
                
                .icon-box-blue {
                    background: #1e3a8a;
                    color: white;
                    padding: 10px;
                    border-radius: 12px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }

                .custom-textarea {
                    border: 1px solid #dee2e6;
                    border-radius: 12px;
                    min-height: 160px;
                    padding: 15px;
                    font-size: 1rem;
                    resize: none;
                    transition: all 0.2s ease;
                    background-color: #fcfdfe;
                }

                .custom-textarea:focus {
                    border-color: #1e3a8a;
                    box-shadow: 0 0 0 4px rgba(30, 58, 138, 0.05);
                    background-color: #fff;
                }

                .btn-navy {
                    background-color: #1e3a8a;
                    color: white;
                    border: none;
                    transition: all 0.3s ease;
                }

                .btn-navy:hover {
                    background-color: #152c6d;
                    color: white;
                    transform: translateY(-1px);
                }

                .btn-navy:disabled {
                    background-color: #cbd5e0;
                    cursor: not-allowed;
                }

                .rounded-4 { border-radius: 1rem !important; }

                .hover-up {
                    transition: transform 0.2s ease;
                }
                .hover-up:active {
                    transform: scale(0.98);
                }
            `}</style>
        </div>
    );
};

export default AdminAnnouncement;